import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { FIELD_LENGTH_M, FIELD_WIDTH_M } from '@4cm/core/tactics';
import type { FieldRegion } from '@4cm/core/tactics/setPieces';

/** SVG viewBox tuple: [minX, minY, width, height]. */
type ViewBox = readonly [number, number, number, number];

/**
 * Counter-scale factor for "marker" elements — player tokens — so they
 * hold a constant on-screen size as the viewBox zooms. 1 at the full
 * pitch, smaller as the set-piece camera pushes in. Without it a zoom
 * bloats every token into a giant disc.
 *
 * Field-space annotations (arrows, zones, lines) deliberately do NOT
 * consume this: they scale with the pitch, which is correct — a 10 m
 * pass is a 10 m pass at any zoom.
 */
export const PitchZoomContext = createContext(1);

/**
 * Glides the SVG viewBox from its current value to `target` whenever
 * `target` changes — a camera move, not a jump cut. A set-piece zoom
 * that snaps instantly reads as cheap; easing it over ~460ms gives the
 * FIFA-style "camera pushes in on the corner" feel.
 *
 * Cheap to run: only the `<svg viewBox>` attribute changes per frame —
 * the board's children are stable element references (their owner
 * isn't re-rendering) so React reconciles them as unchanged. Honours
 * prefers-reduced-motion by jumping straight to the target.
 */
function useAnimatedViewBox(
  target: ViewBox,
): [ViewBox, (next: ViewBox) => void] {
  const [vb, setVb] = useState<ViewBox>(target);
  const vbRef = useRef<ViewBox>(target);
  vbRef.current = vb;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = vbRef.current;
    if (from.every((v, i) => Math.abs(v - target[i]) < 1e-3)) return;

    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVb(target);
      return;
    }

    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const DURATION_MS = 460;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      const e = 1 - Math.pow(1 - t, 3); // easeOutCubic — camera glide
      setVb([
        from[0] + (target[0] - from[0]) * e,
        from[1] + (target[1] - from[1]) * e,
        from[2] + (target[2] - from[2]) * e,
        from[3] + (target[3] - from[3]) * e,
      ]);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // Effect keys on the four viewBox numbers, not the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target[0], target[1], target[2], target[3]]);

  // Immediate, un-animated setter — for interactive wheel/pinch zoom,
  // where a 460ms ease per tick would feel laggy. Cancels any running
  // camera glide so user input always wins.
  const setImmediate = useCallback((next: ViewBox) => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setVb(next);
  }, []);

  return [vb, setImmediate];
}

/**
 * Top-down pitch surface for the 2D tactics board, rendered LANDSCAPE
 * (broadcast convention: length on the horizontal axis).
 *
 * The SVG viewBox uses world metres, with the length axis (105 m)
 * mapped to SVG-X and the width axis (68 m) mapped to SVG-Y. The
 * world-to-SVG mapping is:
 *
 *   SVG-x = -world.z   // home (+z) on the left, away (-z) on the right
 *   SVG-y =  world.x   // unchanged sideline axis
 *
 * The negation on `z` puts home's defending end on the LEFT of the
 * screen so coaches see "home attacking left-to-right" — the
 * standard broadcast read. PlayerToken2D, MovementArrow2D and
 * ArrowDrawLayer2D apply the same swap when consuming/producing world
 * coordinates so persistence stays in world metres (matching the 3D
 * board's `tactics-3d-${id}` localStorage shape).
 *
 * Children are rendered above the pitch markings so player tokens
 * and arrows sit on top.
 */
export interface PitchSvgProps {
  children?: ReactNode;
  /**
   * Optional ref to the SVG element so the composite board can attach
   * a pointer-down listener for the arrow-draw layer (we project
   * pointer events back into world metres there).
   */
  svgRef?: React.RefObject<SVGSVGElement | null>;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
  /** Accessible label override — defaults to a formation-derived string. */
  ariaLabel?: string;
  /** Team labels + formation codes used to build the default aria-label. */
  homeLabel?: string;
  homeFormation?: string;
  awayLabel?: string;
  awayFormation?: string;
  /**
   * Optional crop. When set, the SVG viewBox frames just this slice of
   * the pitch (world metres) instead of the full 105×68 field — used by
   * set-piece mode so a corner shows the attacking third, not the whole
   * pitch. The full pitch markings still render; only the viewBox (and
   * thus the visible window) changes. Omit for the full-pitch view.
   */
  region?: FieldRegion;
  /**
   * When true, dragging empty pitch pans the (zoomed) board. The caller
   * gates this on its own interaction mode — pan must be off while the
   * coach is drawing arrows or zones, where a background drag means
   * something else. A drag that starts on a player token is ignored so
   * token dragging still works.
   */
  panEnabled?: boolean;
}

/**
 * Builds a human-readable aria-label for any pitch SVG. Shared between
 * the interactive 2D board and the static `<Pitch>` mini-view so that
 * a screen reader hears them identically when they describe the same
 * formation pairing.
 *
 *   buildPitchAriaLabel({ homeLabel: 'Aurora FC', homeFormation: '4-3-3' })
 *   → "Aurora FC 4-3-3 vs Away"
 */
export function buildPitchAriaLabel(args: {
  homeLabel?: string;
  homeFormation?: string;
  awayLabel?: string;
  awayFormation?: string;
}): string {
  const home = args.homeLabel ?? 'Home';
  const away = args.awayLabel ?? 'Away';
  const homePart = args.homeFormation ? `${home} ${args.homeFormation}` : home;
  const awayPart = args.awayFormation ? `${away} ${args.awayFormation}` : away;
  return `${homePart} vs ${awayPart}`;
}

const HALF_W = FIELD_WIDTH_M / 2;
const HALF_L = FIELD_LENGTH_M / 2;

// Markings padding so the touchline rectangle sits a metre inside the
// viewBox edge — gives the arrow draw plane a little room to start a
// curve right on the touchline without clipping.
const PADDING = 1;

// Full-pitch viewBox — the zoom-out limit; the wheel handler clamps the
// viewBox so it never pans or zooms past the field.
const FULL_VIEWBOX: ViewBox = [
  -HALF_L - PADDING,
  -HALF_W - PADDING,
  FIELD_LENGTH_M + PADDING * 2,
  FIELD_WIDTH_M + PADDING * 2,
];
// Per-notch wheel zoom factor, and the tightest viewBox width allowed
// (~3.5× zoom-in from the full pitch).
const ZOOM_STEP = 1.18;
const MIN_VBW = (FIELD_LENGTH_M + PADDING * 2) / 3.5;

const STROKE = 'rgba(255, 255, 255, 0.22)';
const FAINT = 'rgba(255, 255, 255, 0.10)';

const PENALTY_BOX_W = 40.32;       // 18 yards (cross-pitch dimension)
const PENALTY_BOX_L = 16.5;        // depth into the pitch
const SIX_YARD_W = 18.32;
const SIX_YARD_L = 5.5;
const PENALTY_SPOT_DISTANCE = 11;  // from the goal line
const CENTRE_CIRCLE_R = 9.15;

export function PitchSvg({
  children,
  svgRef,
  onPointerDown,
  ariaLabel,
  homeLabel,
  homeFormation,
  awayLabel,
  awayFormation,
  region,
  panEnabled = false,
}: PitchSvgProps) {
  // viewBox is landscape: length on SVG-X, width on SVG-Y.
  //
  // World→SVG mapping (see file header): SVG-x = -world.z, SVG-y = world.x.
  // The full-pitch case frames the whole 105×68 field; `region` reframes
  // the viewBox to a slice (set-piece mode) in the same SVG space —
  // children render unchanged because they're in world metres. The
  // change is eased over ~460ms so it reads as a camera move.
  const targetViewBox: ViewBox = region
    ? [
        -region.maxZ - PADDING,
        region.minX - PADDING,
        region.maxZ - region.minZ + PADDING * 2,
        region.maxX - region.minX + PADDING * 2,
      ]
    : [
        -HALF_L - PADDING,
        -HALF_W - PADDING,
        FIELD_LENGTH_M + PADDING * 2,
        FIELD_WIDTH_M + PADDING * 2,
      ];
  const [vb, setViewBoxImmediate] = useAnimatedViewBox(targetViewBox);
  const [vbX, vbY, vbW, vbH] = vb;
  // Counter-scale for tokens: width of the current (animated) viewBox
  // relative to the full pitch. 1 at full pitch, ~0.55 when zoomed onto
  // a corner — tokens multiply their radii by this so they stay a
  // constant size on screen instead of bloating with the zoom.
  const FULL_VBW = FIELD_LENGTH_M + PADDING * 2;
  const tokenScale = vbW / FULL_VBW;

  // ── Wheel / trackpad-pinch zoom ───────────────────────────────────
  // Lets the coach zoom the board in/out, anchored on the pointer.
  // Attached natively (not React onWheel) so we can preventDefault the
  // page scroll — React's wheel listener is passive. The handler reads
  // the live viewBox from a ref so the listener attaches just once.
  const liveVbRef = useRef<ViewBox>(vb);
  liveVbRef.current = vb;
  useEffect(() => {
    const svg = svgRef?.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const ctm =
        typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
      if (!ctm) return;
      const pt =
        typeof svg.createSVGPoint === 'function'
          ? svg.createSVGPoint()
          : new DOMPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const cursor = (pt as SVGPoint).matrixTransform(ctm.inverse());
      const [x, y, w, h] = liveVbRef.current;
      // Scroll up / pinch out → zoom in (smaller viewBox).
      const raw = e.deltaY > 0 ? w * ZOOM_STEP : w / ZOOM_STEP;
      const nextW = Math.max(MIN_VBW, Math.min(FULL_VIEWBOX[2], raw));
      const factor = nextW / w;
      const nextH = h * factor;
      // Keep the point under the cursor fixed while zooming.
      let nextX = cursor.x - (cursor.x - x) * factor;
      let nextY = cursor.y - (cursor.y - y) * factor;
      // Never pan past the pitch edges.
      nextX = Math.max(
        FULL_VIEWBOX[0],
        Math.min(FULL_VIEWBOX[0] + FULL_VIEWBOX[2] - nextW, nextX),
      );
      nextY = Math.max(
        FULL_VIEWBOX[1],
        Math.min(FULL_VIEWBOX[1] + FULL_VIEWBOX[3] - nextH, nextY),
      );
      setViewBoxImmediate([nextX, nextY, nextW, nextH]);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [svgRef, setViewBoxImmediate]);

  // ── Drag-to-pan ───────────────────────────────────────────────────
  // Dragging empty pitch slides the (zoomed) board. A drag that starts
  // on a player token is left alone so token dragging still works; the
  // viewBox width is fixed during a pan so the cursor↔world scale we
  // capture on pointer-down stays valid.
  useEffect(() => {
    const svg = svgRef?.current;
    if (!svg || !panEnabled) return;
    let panning = false;
    let startVb: ViewBox = liveVbRef.current;
    let downSvgX = 0;
    let downSvgY = 0;
    // Inverse CTM captured at pointer-down. Using matrixTransform (not
    // ctm.a) keeps the pan correct even when the container query has
    // rotated the SVG −90° on a narrow board.
    let inv: DOMMatrix | null = null;
    const toSvg = (cx: number, cy: number): { x: number; y: number } => {
      const pt =
        typeof svg.createSVGPoint === 'function'
          ? svg.createSVGPoint()
          : new DOMPoint();
      pt.x = cx;
      pt.y = cy;
      const r = (pt as SVGPoint).matrixTransform(inv ?? new DOMMatrix());
      return { x: r.x, y: r.y };
    };
    const onMove = (e: PointerEvent) => {
      if (!panning) return;
      const cur = toSvg(e.clientX, e.clientY);
      const w = startVb[2];
      const h = startVb[3];
      // Shift the viewBox so the world point grabbed at pointer-down
      // stays under the cursor.
      let nx = startVb[0] + (downSvgX - cur.x);
      let ny = startVb[1] + (downSvgY - cur.y);
      nx = Math.max(
        FULL_VIEWBOX[0],
        Math.min(FULL_VIEWBOX[0] + FULL_VIEWBOX[2] - w, nx),
      );
      ny = Math.max(
        FULL_VIEWBOX[1],
        Math.min(FULL_VIEWBOX[1] + FULL_VIEWBOX[3] - h, ny),
      );
      setViewBoxImmediate([nx, ny, w, h]);
    };
    const onUp = () => {
      panning = false;
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null;
      // Only our board, and never hijack a token drag.
      if (!t || !svg.contains(t)) return;
      if (typeof t.closest === 'function' && t.closest('.t2d-token')) return;
      const ctm =
        typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
      if (!ctm) return;
      panning = true;
      startVb = liveVbRef.current;
      inv = ctm.inverse();
      const down = toSvg(e.clientX, e.clientY);
      downSvgX = down.x;
      downSvgY = down.y;
      // Track move/up on the document in capture phase too — the board
      // stops pointer events from bubbling to window, so a window
      // listener would never fire mid-drag.
      document.addEventListener('pointermove', onMove, true);
      document.addEventListener('pointerup', onUp, true);
    };
    // Capture phase on the document — fires before any target handler,
    // so the pan is never swallowed by an overlay or a child that
    // stops propagation.
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
    };
  }, [svgRef, setViewBoxImmediate, panEnabled]);

  const label =
    ariaLabel ??
    buildPitchAriaLabel({ homeLabel, homeFormation, awayLabel, awayFormation });

  return (
    <svg
      ref={svgRef}
      className="t2d-svg"
      viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
      onPointerDown={onPointerDown}
    >
      <defs>
        {/* Reusable arrowhead — the MovementArrow2D component refers to
            this id by `url(#t2d-ah)`. Sized in metres like the rest. */}
        <marker
          id="t2d-ah"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
        </marker>
        {/* Pitch rectangle clip — referenced by analytical overlays
            (Spacing / pitch control / heatmap) so they never bleed past
            the touchline into the padded viewBox margins. Matches the
            grass rect exactly. */}
        <clipPath id="t2d-pitch-clip">
          <rect
            x={-HALF_L}
            y={-HALF_W}
            width={FIELD_LENGTH_M}
            height={FIELD_WIDTH_M}
          />
        </clipPath>
      </defs>

      {/* Grass — keep it subtle so the player tokens read first. */}
      <rect
        x={-HALF_L}
        y={-HALF_W}
        width={FIELD_LENGTH_M}
        height={FIELD_WIDTH_M}
        fill="rgba(8, 28, 18, 0.65)"
      />

      {/* Touchline rectangle. */}
      <rect
        x={-HALF_L}
        y={-HALF_W}
        width={FIELD_LENGTH_M}
        height={FIELD_WIDTH_M}
        fill="none"
        stroke={STROKE}
        strokeWidth={0.25}
      />

      {/* Halfway line — vertical at SVG-x=0. */}
      <line
        x1={0}
        y1={-HALF_W}
        x2={0}
        y2={HALF_W}
        stroke={STROKE}
        strokeWidth={0.2}
      />

      {/* Centre circle + spot. */}
      <circle cx={0} cy={0} r={CENTRE_CIRCLE_R} fill="none" stroke={STROKE} strokeWidth={0.2} />
      <circle cx={0} cy={0} r={0.4} fill={STROKE} />

      {/* Home (defending) end on the LEFT — world +z → SVG-x = -HALF_L. */}
      <rect
        x={-HALF_L}
        y={-PENALTY_BOX_W / 2}
        width={PENALTY_BOX_L}
        height={PENALTY_BOX_W}
        fill="none"
        stroke={FAINT}
        strokeWidth={0.2}
      />
      <rect
        x={-HALF_L}
        y={-SIX_YARD_W / 2}
        width={SIX_YARD_L}
        height={SIX_YARD_W}
        fill="none"
        stroke={FAINT}
        strokeWidth={0.2}
      />
      <circle
        cx={-HALF_L + PENALTY_SPOT_DISTANCE}
        cy={0}
        r={0.4}
        fill={FAINT}
      />

      {/* Away (defending) end on the RIGHT — world -z → SVG-x = +HALF_L. */}
      <rect
        x={HALF_L - PENALTY_BOX_L}
        y={-PENALTY_BOX_W / 2}
        width={PENALTY_BOX_L}
        height={PENALTY_BOX_W}
        fill="none"
        stroke={FAINT}
        strokeWidth={0.2}
      />
      <rect
        x={HALF_L - SIX_YARD_L}
        y={-SIX_YARD_W / 2}
        width={SIX_YARD_L}
        height={SIX_YARD_W}
        fill="none"
        stroke={FAINT}
        strokeWidth={0.2}
      />
      <circle
        cx={HALF_L - PENALTY_SPOT_DISTANCE}
        cy={0}
        r={0.4}
        fill={FAINT}
      />

      <PitchZoomContext.Provider value={tokenScale}>
        {children}
      </PitchZoomContext.Provider>
    </svg>
  );
}
